import {
  AccountsService,
  GetTeamResponse,
  TeamRole,
} from 'base/accounts_service';
import { Asset } from 'base/asset';
import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction,
} from 'mobx';

export class ManageTeamModel {
  readonly needsToUpgrade: boolean;

  name: string;
  color: string;
  avatar: Asset | undefined;
  logo: Asset | undefined;
  vacantSeats: number;
  readonly members: MemberModel[];
  readonly invites: InviteModel[];
  invitationLinkEnabled: boolean = false;
  invitationLink: string | null = null;
  loadingInvitationLink: boolean = true;

  constructor(
    res: GetTeamResponse,
    private readonly accountsService: AccountsService,
  ) {
    this.needsToUpgrade = res.needsToUpgrade;
    this.name = res.name;
    this.color = res.color;
    this.avatar = res.avatar;
    this.logo = res.logo;
    this.vacantSeats = res.vacantSeats;
    this.members = observable.array(
      res.members.map((m) => new MemberModel(m, accountsService)),
    );
    this.invites = observable.array(
      res.invites.map((i) => new InviteModel(i.email, i.role, accountsService)),
    );
    makeObservable<typeof this, 'me'>(this, {
      name: observable.ref,
      vacantSeats: observable.ref,
      me: computed,
      invitationLink: observable.ref,
      invitationLinkEnabled: observable,
      loadingInvitationLink: observable,
      fetchInviteLinkInfo: action,
      resetInviteLink: action,
      toggleInviteLinks: action,
    });
  }

  private get me() {
    const mes = this.members.filter((m) => m.isMe);
    if (mes.length !== 1) {
      throw new Error(`excepted 1 member with isMe, got ${mes.length}`);
    }
    return mes[0];
  }

  get myRole() {
    return this.me.role;
  }

  async changeName(name: string) {
    await this.accountsService.teamUpdate({ name });
    this.name = name;
  }

  async removeMember(member: MemberModel) {
    await this.accountsService.teamRemoveMember({ member: member.id });
    const index = this.members.indexOf(member);
    if (member.isMe) {
      // Complex to update the model enough... Just reload the entire page.
      window.location.reload();
    }
    if (index !== -1) {
      this.members.splice(index, 1);
      this.vacantSeats++;
    }
  }

  async revokeInvite(invite: InviteModel) {
    await this.accountsService.teamDeleteInvite({ email: invite.email });
    const index = this.invites.indexOf(invite);
    if (index !== -1) this.invites.splice(index, 1);
  }

  async fetchInviteLinkInfo() {
    this.loadingInvitationLink = true;
    try {
      const response = await this.accountsService.teamGetInvitationLinkInfo();
      runInAction(() => {
        this.updateInvitationLinkState(
          response.enabled,
          response.enabled ? response.invitationLink : null,
        );
      });
      return response;
    } catch (error) {
      runInAction(() => {
        this.updateInvitationLinkState(false, null);
      });
      throw error;
    }
  }

  private updateInvitationLinkState(enabled: boolean, link: string | null) {
    this.invitationLinkEnabled = enabled;
    this.invitationLink = link;
    this.loadingInvitationLink = false;
  }

  async resetInviteLink() {
    this.loadingInvitationLink = true;
    const response = await this.accountsService.teamResetInvitationLink();
    this.updateInvitationLinkState(response.enabled, response.invitationLink);
    return this.invitationLink;
  }

  async toggleInviteLinks(enabled: boolean) {
    this.loadingInvitationLink = true;
    try {
      if (enabled) {
        const response =
          await this.accountsService.teamGetOrCreateInvitationLink();
        runInAction(() => {
          this.updateInvitationLinkState(
            response.enabled,
            response.invitationLink,
          );
        });
      } else {
        await this.accountsService.teamDisableInvitationLink();
        runInAction(() => {
          this.updateInvitationLinkState(false, null);
        });
      }

      return this.invitationLink;
    } catch (error) {
      runInAction(() => {
        this.loadingInvitationLink = false;
      });
      throw error;
    }
  }

  get canInvite() {
    return this.myRole === 'admin' && !this.needsToUpgrade;
  }

  readonly invite = async (emails: readonly string[]) => {
    const role = 'member';
    const res = await this.accountsService.teamInvite({ emails, role });
    for (const email of res.emails) {
      this.invites.push(new InviteModel(email, role, this.accountsService));
    }
  };
}

export class MemberModel {
  readonly id: number;
  readonly isMe: boolean;
  readonly name: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly avatar?: Asset;
  readonly color: string;

  readonly email: string;
  role: TeamRole;

  constructor(
    private readonly res: GetTeamResponse['members'][0],
    private readonly accountsService: AccountsService,
  ) {
    this.id = res.id;
    this.isMe = res.isMe;
    this.name = `${res.firstName} ${res.lastName}`.trim();
    this.firstName = res.firstName;
    this.lastName = res.lastName;
    this.avatar = res.avatar;
    this.color = res.color;
    this.email = res.email;
    this.role = res.teamRole;
    makeObservable(this, { role: observable.ref });
  }

  async changeRole(role: TeamRole) {
    if (role === this.role) return;
    await this.accountsService.teamUpdate({
      user: this.res.id,
      role,
    });
    this.role = role;
  }
}

export class InviteModel {
  constructor(
    readonly email: string,
    public role: TeamRole,
    private readonly accountsService: AccountsService,
  ) {
    makeObservable(this, { role: observable.ref });
  }

  async changeRole(role: TeamRole) {
    if (role === this.role) return;
    await this.accountsService.teamChangeInvite({ email: this.email, role });
    this.role = role;
  }

  async resend() {
    await this.accountsService.teamInvite({
      emails: [this.email],
      role: this.role,
    });
  }
}
